import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DefaultImage from '../assets/img/profile.png';
import { API_URL } from "../config.js";
import LoadingSpinner from '../components/LoadingSpinner';
import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams } from 'react-router-dom'; // Add this import

const Profile = () => {
    const [userData, setUserData] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { id } = useParams(); // Get the ID from URL params

    useEffect(() => {
        fetchUserProfile();
    }, [id]); // Re-fetch when ID changes

    const fetchUserProfile = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");
            if (!token) {
                setError("Authentication token not found");
                setLoading(false);
                return;
            }

            // Use query param ?userId=ID if id exists, else fetch current user
            const url = id
                ? `${API_URL}auth/role-detail?userId=${id}`
                : `${API_URL}auth/role-detail`;

            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (response.data.success) {
                setProfileData(response.data.data);

                if (response.data.data) {
                    setUserData({
                        user_fullname: response.data.data.user_fullname,
                        user_designation: response.data.data.user_designation,
                        user_mob: response.data.data.user_mob,
                        user_mob2: response.data.data.user_mob2,
                        user_whatsapp: response.data.data.user_whatsapp,
                        user_email: response.data.data.user_email,
                    });
                }
            } else {
                setError(response.data.message || "Failed to fetch profile data");
            }
        } catch (error) {
            console.error("Error fetching profile data:", error);
            setError(error.response?.data?.message || "An error occurred while fetching profile data");
        } finally {
            setLoading(false);
        }
    };


    // Formatting dates
    const formatDate = (dateString) => {
        if (!dateString || dateString === "0000-00-00" || dateString === "1899-11-30") return "";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB'); // Format: dd-mm-yyyy
        } catch (error) {
            console.error("Error formatting date:", dateString, error);
            return "";
        }
    };

    // Download function like in your EditProfile component
    const handleImageDownload = async (url, filename = "download.jpg") => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");
            const response = await axios.get(url, {
                responseType: "blob",
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            const blob = new Blob([response.data]);
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', filename);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Release memory
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download error:", err);
            // Fallback: open in new tab if download fails
            window.open(url, 'target');
        } finally {
            setLoading(false);
        }
    };

    // Get profile data with fallbacks
    const getProfileData = () => {
        if (!profileData) {
            return {
                user_fullname: userData ? userData.user_fullname : "Loading...",
                user_role: "Employee",
                user_designation: userData ? userData.user_designation : "Loading...",
                user_image: DefaultImage,
                user_mob: userData ? userData.user_mob : "Loading...",
                user_mob2: userData ? userData.user_mob2 : "Loading...",
                user_whatsapp: userData ? userData.user_whatsapp : "Loading...",
                user_email: userData ? userData.user_email : "Loading...",
                office_email: "Loading...",
                user_dob: "",
                user_pan: "",
                user_pan_image: "",
                user_aadhar: "",
                user_aadhar_image: "",
                bank_name: "",
                bank_ifsc: "",
                bank_account: "",
                join_date: "",
                name_in_bank: "",
                esinumber: "",
                esi_image: "",
            };
        }

        return {
            user_fullname: profileData.user_fullname || "Not provided",
            user_role: "Employee",
            user_designation: profileData.user_designation || "Not provided",
            user_image: profileData.user_image || profileData.crm_profile_image || DefaultImage,
            user_mob: profileData.user_mob || "Not provided",
            user_mob2: profileData.user_mob2 || "Not provided",
            user_whatsapp: profileData.user_whatsapp || "Not provided",
            user_email: profileData.user_email || "Not provided",
            office_email: profileData.office_email || "Not provided",
            user_dob: profileData.user_dob,
            user_pan: profileData.user_pan || "Not provided",
            user_pan_image: profileData.user_pan_image,
            user_aadhar: profileData.user_aadhar || "Not provided",
            user_aadhar_image: profileData.user_aadhar_image,
            bank_name: profileData.bank_name || "Not provided",
            bank_ifsc: profileData.bank_ifsc || "Not provided",
            bank_account: profileData.bank_account || "Not provided",
            join_date: profileData.join_date,
            name_in_bank: profileData.name_in_bank || profileData.your_name_in_bank || "Not provided",
            esinumber: profileData.esinumber || "Not provided",
            esi_image: profileData.esi_image,
            user_category: profileData.user_category,
            // Additional fields from API
            user_code: profileData.user_code,
            user_branch: profileData.user_branch,
            user_department: profileData.user_department,
            employmentstatus: profileData.employmentstatus,
            employmenttype: profileData.employmenttype,
            basic_pay: profileData.basic_pay,
            father_name: profileData.father_name,
            nominee_name: profileData.nominee_name,
            nominee_dob: profileData.nominee_dob,
            relationship_with_nominee: profileData.relationship_with_nominee,
            botsense_user: profileData.botsense_user || "Not provided",
            botsense_password: profileData.botsense_password || "Not provided",
            is_mobile_app_lined: profileData.is_mobile_app_lined,
        };
    };

    const profile = getProfileData();

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="panel panel-default with-rounded-corner with-shadow mt-5">
                <div className="alert alert-danger m-3">
                    <strong>Error:</strong> {error}
                </div>
            </div>
        );
    }

    return (
        <div className="panel panel-default with-rounded-corner with-shadow mt-5">
            <div className="profile-header">
                <div className="profile-header-cover"></div>
                <div className="profile-header-content">
                    <div className="profile-header-img" style={{ verticalAlign: 'middle' }}>
                        <img
                            src={profile.user_image}
                            alt={profile.user_fullname}
                            onError={(e) => {
                                e.target.src = DefaultImage;
                            }}
                        />
                    </div>
                    <div className="profile-header-info">
                        <h4 className="m-t-sm">{profile.user_fullname}</h4>
                        <h5 className="m-t-sm">{profile.user_designation}</h5>
                        {profile.user_code && <p className="text-muted" style={{ marginBottom: '5px' }}>Employee Code: {profile.user_code}</p>}
                        
                        
                        
                        {/* {id && <p className="text-muted">Viewing profile of user ID: {id}</p>} */}
                    </div>
                </div>

                {/* Only show action tabs when viewing own profile (no ID in URL) */}
                {!id && (
                    <ul className="profile-header-tab nav-tabs">
                        <li className="nav-item">
                            <a href="/profile/change-password" className="nav-link active">Change Password</a>
                        </li>
                        <li className="nav-item">
                            <a href="/profile/edit-profile" className="nav-link active">Edit Profile</a>
                        </li>

                        {/* Conditional: Download Payslip if user_category = 1 */}
                        {/* {profile.user_category === "1" && (
                            <li className="nav-item">
                                <a href="/profile/Payslip" className="nav-link active">Download Payslip</a>
                            </li>
                        )} */}

                        {/* Conditional: Download ESI File if user_category = 0 or 2 */}
                        {/* {(profile.user_category === "0" || profile.user_category === "2") && (
                            <li className="nav-item">
                                <button
                                    onClick={() => handleImageDownload(profile.esi_image, 'esi_file.pdf')}
                                    className="nav-link active btn btn-link"
                                    style={{ border: 'none', background: 'none', color: 'inherit', textDecoration: 'none' }}
                                >
                                    Download ESI File
                                </button>
                            </li>
                        )} */}
                    </ul>
                )}
            </div>

            <div className="profile-container">
                <div className="row row-space-20">
                    <div className="col-md-6">
                        <div className="tab-content p-0">
                            <div className="tab-pane fade in active" id="profile-about">
                                <table className="table table-profile">
                                    <thead>
                                        <tr>
                                            <th colSpan="2">PROFILE INFORMATION</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="field">Personal Mobile</td>
                                            <td className="value"><b>{profile.user_mob}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">Office Mobile</td>
                                            <td className="value"><b>{profile.user_mob2}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">WhatsApp No.</td>
                                            <td className="value"><b>{profile.user_whatsapp}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">Personal Email Id</td>
                                            <td className="value"><b>{profile.user_email}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">Office Email</td>
                                            <td className="value"><b>{profile.office_email}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">Joining Date</td>
                                            <td className="value"><b>{formatDate(profile.join_date)}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">Designation</td>
                                            <td className="value"><b>{profile.user_designation}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">Botsense User</td>
                                            <td className="value"><b>{profile.botsense_user}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">Botsense Password</td>
                                            <td className="value"><b>{profile.botsense_password}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">Mobile App Linked</td>
                                            <td className="value">
                                                <span 
                                                    style={{
                                                        display: 'inline-block',
                                                        padding: '4px 8px',
                                                        fontSize: '11px',
                                                        fontWeight: 'medium',
                                                        color: '#fff',
                                                        backgroundColor: profile.is_mobile_app_lined === 1 ? '#28a745' : '#dc3545',
                                                        borderRadius: '4px',
                                                        marginTop: '2px'
                                                    }}
                                                >
                                                    {profile.is_mobile_app_lined === 1 ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="tab-content p-0">
                            <div className="tab-pane fade in active" id="profile-about">
                                <table className="table table-profile">
                                    <thead>
                                        <tr>
                                            <th colSpan="2">ADDITIONAL INFORMATION</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="field">Date of Birth</td>
                                            <td className="value"><b>{formatDate(profile.user_dob)}</b></td>
                                        </tr>
                                        {profile.father_name && (
                                            <tr>
                                                <td className="field">Father's Name</td>
                                                <td className="value"><b>{profile.father_name}</b></td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td className="field">Pancard</td>
                                            <td className="value" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <b>{profile.user_pan}</b>
                                                {profile.user_pan_image && (
                                                    <button
                                                        className="btn btn-default btn-xs"
                                                        onClick={() => handleImageDownload(profile.user_pan_image, "pancard.jpg")}
                                                        style={{ marginLeft: '10px' }}
                                                    >
                                                        <FontAwesomeIcon icon={faDownload} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="field">Aadhar Card</td>
                                            <td className="value" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <b>{profile.user_aadhar}</b>
                                                {profile.user_aadhar_image && (
                                                    <button
                                                        className="btn btn-default btn-xs"
                                                        onClick={() => handleImageDownload(profile.user_aadhar_image, "aadhar_card.jpg")}
                                                        style={{ marginLeft: '10px' }}
                                                    >
                                                        <FontAwesomeIcon icon={faDownload} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="field">Bank Name</td>
                                            <td className="value"><b>{profile.bank_name}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">IFSC Code</td>
                                            <td className="value"><b>{profile.bank_ifsc}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">Account No.</td>
                                            <td className="value"><b>{profile.bank_account}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">Name in Bank</td>
                                            <td className="value"><b>{profile.name_in_bank}</b></td>
                                        </tr>
                                        <tr>
                                            <td className="field">ESI Number</td>
                                            <td className="value" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <b>{profile.esinumber}</b>
                                                {profile.esi_image && (
                                                    <button
                                                        className="btn btn-default btn-xs"
                                                        onClick={() => handleImageDownload(profile.esi_image, "esi_file.pdf")}
                                                    >
                                                        <FontAwesomeIcon icon={faDownload} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {profile.nominee_name && (
                                            <tr>
                                                <td className="field">Nominee Name</td>
                                                <td className="value"><b>{profile.nominee_name}</b></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;